import { parseDbTimestamp } from "@repo/shared";
import type { FlowProcessMessage, FlowProcessMessageType, Translated } from "@repo/shared/types";
import { MessageCircle, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { TimeAgoTooltip } from "@/components/ui/time-ago-tooltip";
import { useT } from "@/lib/i18n/use-t";
import { getRelativeTime } from "@/lib/utils";
import { T } from "./feedback-card.i18n";

/** Longer than this and the comment collapses behind "Show more". */
const COLLAPSE_AT = 280;

/** Source and outcome, plus which round it came from when that is not the current one. */
function feedbackHeading(
  type: FlowProcessMessageType,
  historical: boolean,
  t: Translated<typeof T>,
): string {
  const base = t.heading[type];
  return historical ? `${base} · ${t.historicalSuffix}` : base;
}

type FeedbackCardProps = {
  message: FlowProcessMessage;
  /** For the relative "2 hours ago" time only; the words follow `useT`. */
  language: string;
  /**
   * Show the shared "Reopen the flow to update the advice…" instruction. The
   * caller decides once for the whole stack: it belongs on a locked flow that
   * can actually be reopened, and nowhere else.
   */
  showReopenInstruction: boolean;
  onDismiss: (messageId: string) => void;
};

/**
 * One piece of feedback.
 *
 * Every card gets the same treatment — red heading, message icon, left accent
 * — whatever it says and however old it is. Red marks "read this", not
 * "rejected": a remark on completed advice looks exactly like a rejection,
 * because both are things somebody wrote that the advisor has not closed yet.
 */
export function FeedbackCard({
  message,
  language,
  showReopenInstruction,
  onDismiss,
}: FeedbackCardProps) {
  const t = useT(T);
  const [expanded, setExpanded] = useState(false);

  const comment = message.comment?.trim() || null;
  const collapsible = !!comment && comment.length > COLLAPSE_AT;
  const shown = collapsible && !expanded ? `${comment.slice(0, COLLAPSE_AT).trimEnd()}…` : comment;

  return (
    <div className="rounded-lg border border-l-4 border-l-destructive bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <p className="flex items-center gap-2 font-semibold text-destructive">
          <MessageCircle className="h-4 w-4 shrink-0" />
          {feedbackHeading(message.type, message.historical, t)}
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm text-muted-foreground">
            {/* Only a recorded author is shown — never a guess at who wrote it. */}
            {message.actorName ? `${message.actorName} · ` : ""}
            <TimeAgoTooltip date={parseDbTimestamp(message.createdAt)}>
              {getRelativeTime(message.createdAt, language)}
            </TimeAgoTooltip>
          </span>
          <Button
            size="icon"
            variant="ghost"
            tooltip={t.dismiss}
            onClick={() => onDismiss(message.id)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <p className="mt-2 whitespace-pre-wrap">
        {shown ?? <span className="text-muted-foreground italic">{t.noComment}</span>}
      </p>
      {collapsible && (
        <Button
          variant="link"
          className="mt-1 h-auto p-0 text-sm"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? t.showLess : t.showMore}
        </Button>
      )}

      {showReopenInstruction && (
        <p className="mt-2 text-sm text-muted-foreground">{t.reopenInstruction}</p>
      )}
    </div>
  );
}
